import React, { useState } from "react";
import {Pause,Play,RefreshCw,XCircle,AlertTriangle} from "lucide-react";
function QuickActions({ conversation, loading = false, onPause, onResume, onReset, onRestart, onClose,}) {
  const [confirmAction, setConfirmAction] = useState(null);
  const status = conversation?.status || conversation?.conversation_status || "ACTIVE";
  const normalizedStatus = status?.toUpperCase();
  const isPaused = normalizedStatus === "PAUSED";
  const isClosed = normalizedStatus === "CLOSED";

  const actions = [
    {
      key: "pause",
      label: "Pause",
      icon: Pause,
      description: "Pause automation",
      visible: !isPaused && !isClosed,
      color:
        "text-[#b7791f] hover:border-[#f3d9a5] hover:bg-[#fffaf0]",
      iconBg:
        "bg-[#fff8e8] text-[#c58a2a] group-hover:bg-[#fff1cf]",
    },
    {
      key: "resume",
      label: "Resume",
      icon: Play,
      description: "Resume automation",
      visible: !isPaused,
      color:
        "text-[#147db5] hover:border-[#b9def1] hover:bg-[#f3faff]",
      iconBg:
        "bg-[#eaf6fd] text-[#3d9bd2] group-hover:bg-[#dff2fb]",
    },
    {
      key: "restart",
      label: "Restart",
      icon: RefreshCw,
      description: "Restart conversation",
      visible: !isClosed,
      confirm: true,
      color:
        "text-[#7167a8] hover:border-[#d8d2f0] hover:bg-[#f8f6ff]",
      iconBg:
        "bg-[#f1effb] text-[#756cb0] group-hover:bg-[#e9e6f8]",
    },
    {
      key: "close",
      label: "Close",
      icon: XCircle,
      description: "Close conversation",
      visible: !isClosed,
      confirm: true,
      color:
        "text-[#c45d68] hover:border-[#f0c8cd] hover:bg-[#fff7f8]",
      iconBg:
        "bg-[#fff0f2] text-[#c86670] group-hover:bg-[#ffe6e9]",
    },
  ];

  const executeAction = async (key) => {
    if (loading) return;

    try {
      switch (key) {
        case "pause":
          await onPause?.();
          break;

        case "resume":
          await onResume?.();
          break;

        case "restart":
          await onRestart?.();
          break;

        case "close":
          await onClose?.();
          break;

        default:
          break;
      }
    } finally {
      setConfirmAction(null);
    }
  };

  const handleAction = (action) => {
    if (loading) return;

    if (action.confirm) {
      setConfirmAction(action.key);
      return;
    }

    executeAction(action.key);
  };

  /*
   * Confirmation state
   */
  if (confirmAction) {
    const action = actions.find(
      (item) => item.key === confirmAction
    );

    if (action) {
      const Icon = action.icon;

      const isClose = action.key === "close";

      return (
        <div className="rounded-md border border-[#f0d5d8] bg-[#fff9fa] p-3 shadow-[0_2px_10px_rgba(196,93,104,0.05)] dark:border-red-900/40 dark:bg-[#21181b]">
          <div className="flex items-start gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#f0dfe1] bg-white text-[#c86670] shadow-[0_1px_4px_rgba(80,60,60,0.04)] dark:border-red-900/40 dark:bg-[#182631]">
              <AlertTriangle
                size={15}
                strokeWidth={1.8}
              />
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold text-[#394850] dark:text-dark-text-primary">
                {action.label} conversation?
              </p>

              <p className="mt-0.5 text-[8px] leading-3.5 text-[#7d8a91] dark:text-dark-text-muted">
                This action will update the conversation runtime.
              </p>

              <div className="mt-2.5 flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => setConfirmAction(null)}
                  className="rounded-lg border border-[#dfe6ea] bg-white px-2.5 py-1.5 text-[8px] font-semibold text-[#71808a] transition-all duration-200 hover:border-[#cbd8de] hover:bg-[#f8fafb] hover:text-[#52636d] disabled:cursor-not-allowed disabled:opacity-50 dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-secondary"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={loading}
                  onClick={() =>
                    executeAction(action.key)
                  }
                  className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[8px] font-semibold text-white transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${
                    isClose
                      ? "bg-[#c86670] hover:bg-[#b95862]"
                      : "bg-[#147db5] hover:bg-[#0f6c9d]"
                  }`}
                >
                  <Icon
                    size={10}
                    strokeWidth={2}
                  />

                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  const visibleActions = actions.filter(
    (action) => action.visible
  );

  /*
   * Normal state
   */
  return (
    <div className="rounded-xl border border-[#e3edf3] bg-[#f8fbfd] p-1.5 shadow-[0_1px_4px_rgba(61,155,210,0.035)] dark:border-dark-border dark:bg-[#111c26]">
      <div
        className={`grid gap-1.5 ${
          visibleActions.length <= 2
            ? "grid-cols-2"
            : "grid-cols-2 sm:grid-cols-4"
        }`}
      >
        {visibleActions.map((action) => {
          const Icon = action.icon;

          return (
            <button
              key={action.key}
              type="button"
              disabled={loading}
              onClick={() => handleAction(action)}
              title={action.description}
              className={`group relative flex min-w-0 flex-col items-center justify-center rounded-lg border border-transparent bg-white px-1.5 py-2.5 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_4px_12px_rgba(61,155,210,0.07)] active:translate-y-0 dark:bg-[#182631] dark:hover:bg-[#1c2d39] disabled:cursor-not-allowed disabled:opacity-50 ${action.color}`}
            >
              <div
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all duration-200 group-hover:scale-[1.04] ${action.iconBg}`}
              >
                <Icon
                  size={14}
                  strokeWidth={1.8}
                />
              </div>

              <span className="mt-1.5 max-w-full truncate text-[8px] font-semibold">
                {action.label}
              </span>

              <span className="mt-0.5 max-w-full truncate text-[7px] text-[#9aa7ae] dark:text-dark-text-muted">
                {action.description}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default QuickActions;